export interface SelectionOption {
  value: string;
  label: string;
}

export const SEGMENT_OPTIONS: SelectionOption[] = [
  { value: 'NONE', label: 'No Segment Selected' },
  { value: 'BUILDING', label: 'Building and/or Remodeling' },
  { value: 'COLLEGE', label: 'College / University' },
  { value: 'DESIGN', label: 'Design' },
  { value: 'IP', label: 'Industry Partner' },
  { value: 'MANUFACTURING', label: 'Manufacturing' },
  { value: 'RETAIL', label: 'Retail' },
  { value: 'OTHER', label: 'Other' },
];

export const SUBSEGMENT_OPTIONS: Record<string, SelectionOption[]> = {
  BUILDING: [{ value: 'BUILDING', label: 'Building / Remodeling' }, { value: 'SKILLED', label: 'Skilled Trades' }],
  DESIGN: [{ value: 'DESIGN', label: 'Design' }],
  MANUFACTURING: [{ value: 'CABINET', label: 'Cabinet Shops' }, { value: 'MANUFACTURING', label: 'Manufacturing' }],
  RETAIL: [
    { value: 'DEALER', label: 'Dealer Showroom' },
    { value: 'RETAIL', label: 'Retail' },
    { value: 'WHOLESALE', label: 'Wholesale / Distribution' },
  ],
};

export const FUNCTIONAL_ROLE_OPTIONS: Record<string, SelectionOption[]> = {
  DESIGN: [
    { value: 'BATH', label: 'Bath Designer' },
    { value: 'INTERIOR', label: 'Interior Designer' },
    { value: 'KITCHENBATH', label: 'Kitchen & Bath Designer' },
    { value: 'KITCHEN', label: 'Kitchen Designer' },
    { value: 'LIGHTING', label: 'Lighting Designer' },
    { value: 'PROJECT', label: 'Project Coordinator / Manager' },
    { value: 'SALESPERSON', label: 'Salesperson' },
    { value: 'SHOWROOM', label: 'Showroom Manager / Staff' },
    { value: 'OTHER', label: 'Other' },
  ],
  BUILDING: [
    { value: 'ARCHITECT', label: 'Architect' },
    { value: 'BUILDER', label: 'Builder' },
    { value: 'CARPENTER', label: 'Carpenter' },
    { value: 'INTERIOR', label: 'Interior Designer' },
    { value: 'ELECTRICIAN', label: 'Electrician' },
    { value: 'FABRICATOR', label: 'Fabricator' },
    { value: 'GM', label: 'General Manager' },
    { value: 'HOME', label: 'Home Technology Integrator / Professional' },
    { value: 'INSTALLER', label: 'Installer' },
    { value: 'KITCHENBATH', label: 'Kitchen & Bath Designer' },
    { value: 'PLUMBER', label: 'Plumber' },
    { value: 'PROJECT', label: 'Project Coordinator / Manager' },
    { value: 'REMODELER', label: 'Remodeler' },
    { value: 'SALESPERSON', label: 'Salesperson' },
    { value: 'SHOWROOM', label: 'Showroom Manager / Staff' },
    { value: 'SKILLED', label: 'Skilled Trades Professional' },
    { value: 'OTHER', label: 'Other' },
  ],
  MANUFACTURING: [
    { value: 'APPRENTICE', label: 'Apprentice' },
    { value: 'CORPORATE', label: 'Corporate Role (Marketing, Product Development, Purchasing, etc.)' },
    { value: 'CUSTOMERSERVICE', label: 'Customer Service / Product Training / Technical Support' },
    { value: 'DEDICATED', label: 'Dedicated Manufacturer Rep / Factory Rep (representing single brand)' },
    { value: 'DISPLAYSHOWROOM', label: 'Display Showroom Manager / Staff' },
    { value: 'INDEPENDENT', label: 'Independent Manufacturer Rep (representing multiple brands)' },
    { value: 'INTERIOR', label: 'Interior Designer' },
    { value: 'KITCHENBATH', label: 'Kitchen & Bath Designer' },
    { value: 'MASTER', label: 'Master Carpenter' },
    { value: 'PROJECT', label: 'Project Coordinator / Manager' },
    { value: 'OTHER', label: 'Other' },
  ],
  RETAIL: [
    { value: 'BATH', label: 'Bath Designer' },
    { value: 'CORPORATE', label: 'Corporate Role (Marketing, Product Development, Purchasing, etc.)' },
    { value: 'CUSTOMERSERVICE', label: 'Customer Service / Product Training / Technical Support' },
    { value: 'DISPLAYSHOWROOM', label: 'Display Showroom Manager / Staff' },
    { value: 'INDEPENDENTSHOWROOM', label: 'Independent Kitchen & Bath Showroom Manager / Staff' },
    { value: 'INTERIOR', label: 'Interior Designer' },
    { value: 'KITCHENBATH', label: 'Kitchen & Bath Designer' },
    { value: 'KITCHEN', label: ' Kitchen Designer' },
    { value: 'LIGHTING', label: 'Lighting Designer' },
    { value: 'MERCHANDISING', label: 'Merchandising Manager / Staff' },
    { value: 'MULTIBRANCH', label: 'Multi-Branch Retailer Manager / Staff' },
    { value: 'ONLINE', label: 'Online K&B Seller Manager / Staff' },
    { value: 'PROJECT', label: 'Project Coordinator / Manager' },
    { value: 'PURCHASING', label: 'Purchasing Manager / Staff' },
    { value: 'SALESPERSON', label: 'Salesperson' },
    { value: 'WAREHOUSE', label: 'Warehouse Manager' },
    { value: 'WHOLESALE', label: 'Wholesale Manager / Staff' },
    { value: 'WHOLESALESHOWROOM', label: 'Wholesale Showroom Manager / Staff' },
    { value: 'OTHER', label: 'Other' },
  ],
  COLLEGE: [
    { value: 'EDUCATOR', label: 'Educator' },
    { value: 'STUDENT', label: 'Student' },
    { value: 'OTHER', label: 'Other' },
  ],
};

export function getSegmentLabel(value: string | undefined): string {
  if (!value || value === 'NONE') {
    return 'No Segment Assigned';
  }
  const option = SEGMENT_OPTIONS.find(o => o.value === value);
  return option ? option.label : value;
}

export function getSubsegmentLabel(value: string | undefined): string {
  if (!value || value === 'NONE') {
    return 'No Subsegment Assigned';
  }
  for (const segment in SUBSEGMENT_OPTIONS) {
    if (Object.prototype.hasOwnProperty.call(SUBSEGMENT_OPTIONS, segment)) {
      const option = SUBSEGMENT_OPTIONS[segment].find(o => o.value === value);
      if (option) {
        return option.label;
      }
    }
  }
  return value;
}

export function getFunctionalRoleLabel(value: string | undefined): string {
  if (!value || value === 'NONE') {
    return 'No Functional Role Selected';
  }
  for (const segment in FUNCTIONAL_ROLE_OPTIONS) {
    if (Object.prototype.hasOwnProperty.call(FUNCTIONAL_ROLE_OPTIONS, segment)) {
      const option = FUNCTIONAL_ROLE_OPTIONS[segment].find(o => o.value === value);
      if (option) {
        return option.label;
      }
    }
  }
  return value;
}

export const ORGANIZATION_LEVEL_OPTIONS: SelectionOption[] = [
  { value: 'NONE', label: 'No Organization Level selected' },
  { value: 'OWNER', label: 'Owner / Partner' },
  { value: 'PRESIDENT', label: 'President / C-Suite Executive' },
  { value: 'EXECUTIVE', label: 'Executive (SVP, EVP, VP)' },
  { value: 'MIDDLE', label: 'Middle Management (Director / Manager)' },
  { value: 'STAFF', label: 'Staff Role' },
  { value: 'ADMINISTRATIVE', label: 'Administrative Role' },
  { value: 'OTHER', label: 'Other' },
];

export function getOrganizationLevelLabel(value: string | undefined): string {
  if (!value || value === 'NONE') {
    return 'No Organization Level Assigned';
  }
  const option = ORGANIZATION_LEVEL_OPTIONS.find(o => o.value === value);
  return option ? option.label : value;
}



// WEBPACK FOOTER //
// ./src/common/utils/memberConstants.ts